import { getToken } from "firebase/app-check";

import { getAppCheckInstance } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";
import { entityFunctionUrl } from "./EntityService";

// Public calls to the `entityAuth` Cloud Function: detecting how an email signs
// in, requesting and confirming the sign-in code, and accepting an invitation.
//
// They are public — the caller has no session yet, that is the point — so they
// are signed with App Check, like the intake and the contact OTP.

const authUrl = () =>
  process.env.REACT_APP_ENTITY_AUTH_URL || entityFunctionUrl("entityAuth");

async function appCheckToken() {
  const instance = getAppCheckInstance();
  if (!instance) throw new Error("App Check no está inicializado");
  const { token } = await getToken(instance, true);
  if (!token) throw new Error("Token de App Check no disponible");
  return token;
}

// Spanish messages for what can go wrong. Anything else falls through to the
// generic one: an untranslated error is worse than a vague one, because it looks
// like data.
export const ENTITY_AUTH_ERRORS = {
  OTP_INVALID: "El código no es correcto.",
  OTP_EXPIRED: "El código venció. Pide uno nuevo.",
  OTP_MAX_ATTEMPTS: "Demasiados intentos. Pide un código nuevo.",
  RATE_LIMIT: "Demasiados intentos. Espera unos minutos.",
  EMAIL_INVALID: "El correo no es válido.",
  INVITE_INVALID: "La invitación no es válida.",
  INVITE_EXPIRED: "La invitación venció. Pídele a la empresa que te reenvíe una.",
  INVITE_USED: "Esta invitación ya se usó.",
  EMAIL_NOT_VERIFIED: "Primero confirma el código que te llegó al correo.",
  PHONE_NOT_VERIFIED: "Primero verifica tu número de celular.",
  EMAIL_IS_STAFF: "Ese correo ya tiene una cuenta interna. Usa otro.",
  EMAIL_IN_USE: "Ese correo ya pertenece a otra empresa.",
  ACCOUNT_CREATE_FAILED: "No fue posible crear la cuenta. Intenta de nuevo.",
  UNAUTHENTICATED: "Tu sesión venció. Vuelve a entrar e inténtalo de nuevo.",
  SAME_EMAIL: "Ese ya es tu correo.",
  INVALID_EMAIL: "El correo no es válido.",
  RATE_LIMIT_IP: "Demasiados intentos desde esta conexión. Espera unos minutos.",
  RATE_LIMIT_EMAIL: "Demasiados intentos. Espera unos minutos.",
  VALIDATION_ERROR: "Revisa los datos e inténtalo de nuevo.",
};

export const entityAuthErrorLabel = (code) =>
  ENTITY_AUTH_ERRORS[code] || "No fue posible completar la operación. Intenta de nuevo.";

async function call(body, extraHeaders = {}) {
  const token = await appCheckToken();
  const response = await fetch(authUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Firebase-AppCheck": token,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok && data.result === true, status: response.status, data };
}

// How this address should sign in: with a password (internal staff and any
// unknown address) or with a code (company users).
//
// On any failure this answers "password": falling back to the usual flow is the
// only thing that guarantees a problem in this function never locks anyone out
// of the app.
export const checkLoginMode = async (email) => {
  try {
    const { ok, data } = await call({ action: "CHECK_LOGIN_MODE", email });
    return ok && data.mode === "otp" ? "otp" : "password";
  } catch (error) {
    debugLog("checkLoginMode error: ", error);
    return "password";
  }
};

export const sendLoginCode = async (email) => {
  try {
    const { ok, data } = await call({ action: "LOGIN_SEND_OTP", email });
    return ok
      ? { ok: true, expiresInMinutes: data.expiresInMinutes }
      : { ok: false, error: entityAuthErrorLabel(data.error) };
  } catch (error) {
    debugLog("sendLoginCode error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

// Returns the custom token the client uses to open the Firebase session.
export const verifyLoginCode = async (email, code) => {
  try {
    const { ok, data } = await call({ action: "LOGIN_VERIFY_OTP", email, code });
    return ok
      ? { ok: true, customToken: data.customToken }
      : { ok: false, error: entityAuthErrorLabel(data.error) };
  } catch (error) {
    debugLog("verifyLoginCode error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

export const previewInvite = async (token) => {
  try {
    const { ok, data } = await call({ action: "INVITE_PREVIEW", token });
    return ok
      ? { ok: true, invite: data }
      : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("previewInvite error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

// A code to the invitee's address. The address is not sent: it comes from the
// invitation, so nobody holding a valid link can divert the code elsewhere.
export const sendInviteCode = async (token) => {
  try {
    const { ok, data } = await call({ action: "INVITE_SEND_OTP", token });
    return ok
      ? { ok: true, expiresInMinutes: data.expiresInMinutes }
      : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("sendInviteCode error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

export const verifyInviteCode = async (token, code) => {
  try {
    const { ok, data } = await call({ action: "INVITE_VERIFY_OTP", token, code });
    return ok ? { ok: true } : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("verifyInviteCode error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

// The address does NOT travel: the invitation token fixes it and the backend
// reads it from there. Sending it from the form would offer a field the server
// ignores — or worse, one someone believed they could change.
export const acceptInvite = async ({ token, profile, phoneToken }) => {
  try {
    const { ok, data } = await call(
      { action: "ACCEPT_INVITE", token, profile },
      phoneToken ? { "X-Phone-Token": phoneToken } : {}
    );
    return ok
      ? { ok: true, customToken: data.customToken, alreadyMember: !!data.alreadyMember }
      : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("acceptInvite error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

// Cambiar el correo de acceso. A diferencia del resto del archivo, estas dos
// llamadas van AUTENTICADAS: quien pide el cambio ya tiene sesión, y con eso
// prueba que el correo actual es suyo. Lo que hay que probar es el nuevo, y por
// eso el código se envía allá.
export const sendEmailChangeCode = async (newEmail, user) => {
  try {
    const { ok, data } = await call(
      { action: "CHANGE_EMAIL_SEND_OTP", newEmail },
      { Authorization: `Bearer ${user.token}` }
    );
    return ok
      ? { ok: true, expiresInMinutes: data.expiresInMinutes }
      : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("sendEmailChangeCode error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};

export const confirmEmailChange = async ({ newEmail, code, user }) => {
  try {
    const { ok, data } = await call(
      { action: "CHANGE_EMAIL_CONFIRM", newEmail, code },
      { Authorization: `Bearer ${user.token}` }
    );
    return ok
      ? { ok: true, email: data.email }
      : { ok: false, error: entityAuthErrorLabel(data.error), code: data.error };
  } catch (error) {
    debugLog("confirmEmailChange error: ", error);
    return { ok: false, error: entityAuthErrorLabel() };
  }
};
